import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/primitives/Badge';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { 
  User, 
  MapPin, 
  Truck, 
  Shield, 
  CheckCircle2, 
  FileText,
  Zap,
  Clock
} from 'lucide-react';
import { SEO, SEO_CONFIG } from '@/components/primitives/SEO';
import { canadianCities, getAllProvinces, getCitiesByProvince } from '@/data/cities';

export function ApplyPage() {
  const [step, setStep] = useState(1);
  const [tier, setTier] = useState<'neighbour' | 'pro' | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    equipment: '',
    hasInsurance: false,
    hasLicense: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const provinces = getAllProvinces();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would call the API
    console.log('Application submitted:', { tier, ...formData });
    setSubmitted(true);
  };

  return (
    <>
      <SEO {...SEO_CONFIG.apply} />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white overflow-hidden py-16">
        {/* Background Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Gradient Orb */}
        <motion.div 
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/30 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative container-default mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="accent" className="mb-6 bg-accent-500/20 border border-accent-500/30 text-accent-500">
              Join Our Team
            </Badge>

            <h1 className="text-4xl md:text-5xl font-black mb-6">
              Become a SNOWBLOWR Operator
            </h1>

            <p className="text-lg text-snow-100 max-w-2xl mx-auto mb-8">
              <strong className="text-white">Turn your winter equipment into income.</strong> Fast approval, 
              flexible hours, weekly payouts. Join 500+ operators already earning.
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap items-center justify-center gap-8 mb-8">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-accent-500" />
                <span className="text-sm font-medium">24-48h Approval</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent-500" />
                <span className="text-sm font-medium">75-85% Earnings</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-accent-500" />
                <span className="text-sm font-medium">Stripe Verified</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Form */}
      <Section
        eyebrow="Application"
        title={submitted ? "Application Submitted!" : "Apply in 3 minutes"}
        subtitle={submitted ? "We'll review your application and get back to you within 24-48 hours." : "Choose your tier and complete the form below. No commitment until you're approved."}
      >
        {submitted ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-2xl mx-auto mt-12"
          >
            <Card variant="solid" className="text-center py-16">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-24 h-24 mx-auto mb-8 rounded-full bg-accent-500 flex items-center justify-center"
              >
                <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={3} />
              </motion.div>
              
              <h3 className="text-3xl font-black mb-4">Thanks for applying, {formData.fullName}!</h3>
              
              <p className="text-lg text-neutral-600 mb-8 max-w-lg mx-auto">
                We're reviewing your {tier === 'pro' ? 'Pro' : 'Neighbour'} tier application. 
                Check your email at <strong className="text-brand-500">{formData.email}</strong> for next steps.
              </p>

              <div className="bg-neutral-50 rounded-xl p-6 mb-8">
                <h4 className="font-bold mb-4">What happens next?</h4>
                <div className="space-y-3 text-left max-w-md mx-auto">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</div>
                    <p className="text-sm text-neutral-700">Background check via Stripe Identity (24-48h)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</div>
                    <p className="text-sm text-neutral-700">Download the operator app and complete onboarding</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-accent-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</div>
                    <p className="text-sm text-neutral-700">Go online and start accepting jobs!</p>
                  </div>
                </div>
              </div>

              <Button variant="secondary" size="lg" onClick={() => window.location.href = '/'}>
                Back to home
              </Button>
            </Card>
          </motion.div>
        ) : (
          <div className="max-w-3xl mx-auto mt-12">
            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-neutral-600">Application Progress</span>
                <span className="text-sm font-black text-brand-500">{step === 1 ? '0%' : '50%'}</span>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-brand-500 to-accent-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: step === 1 ? '0%' : '50%' }}
                />
              </div>
              <div className="flex justify-between mt-1 text-xs text-neutral-500">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Step 1: Choose Tier */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-black text-center mb-8">Choose Your Tier</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Neighbour Tier */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setTier('neighbour')}
                    className={`cursor-pointer transition-all ${
                      tier === 'neighbour' 
                        ? 'ring-4 ring-brand-500 shadow-xl' 
                        : 'ring-2 ring-neutral-200 hover:ring-brand-300'
                    }`}
                  >
                    <Card variant="outline" className="h-full">
                      <div className="flex items-center justify-between mb-4">
                        <Badge variant="default">Casual</Badge>
                        {tier === 'neighbour' && (
                          <CheckCircle2 className="w-6 h-6 text-brand-500" />
                        )}
                      </div>
                      
                      <h4 className="text-2xl font-black mb-2">Neighbour Tier</h4>
                      <p className="text-sm text-neutral-600 mb-4">Perfect for side income</p>
                      
                      <div className="space-y-2 mb-6">
                        <div className="flex items-center gap-2 text-sm">
                          <Truck className="w-4 h-4 text-brand-500" />
                          <span>Shovel or snowblower</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-brand-500" />
                          <span>18+ with valid ID</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Shield className="w-4 h-4 text-brand-500" />
                          <span>Platform insurance included</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t">
                        <p className="text-2xl font-black text-brand-500">75%</p>
                        <p className="text-xs text-neutral-600">of each job</p>
                      </div>
                    </Card>
                  </motion.div>

                  {/* Pro Tier */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setTier('pro')}
                    className={`cursor-pointer transition-all ${
                      tier === 'pro' 
                        ? 'ring-4 ring-accent-500 shadow-xl' 
                        : 'ring-2 ring-neutral-200 hover:ring-accent-300'
                    }`}
                  >
                    <Card variant="outline" className="h-full">
                      <div className="flex items-center justify-between mb-4">
                        <Badge variant="accent">Commercial</Badge>
                        {tier === 'pro' && (
                          <CheckCircle2 className="w-6 h-6 text-accent-500" />
                        )}
                      </div>
                      
                      <h4 className="text-2xl font-black mb-2">Pro Tier</h4>
                      <p className="text-sm text-neutral-600 mb-4">For serious operators</p>
                      
                      <div className="space-y-2 mb-6">
                        <div className="flex items-center gap-2 text-sm">
                          <Truck className="w-4 h-4 text-accent-500" />
                          <span>Two-stage blower or plow</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-accent-500" />
                          <span>Business license (GST)</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Shield className="w-4 h-4 text-accent-500" />
                          <span>Liability insurance required</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t">
                        <p className="text-2xl font-black text-accent-500">85%</p>
                        <p className="text-xs text-neutral-600">of each job + bigger jobs</p>
                      </div>
                    </Card>
                  </motion.div>
                </div>

                <div className="text-center">
                  <Button
                    variant="primary"
                    size="lg"
                    disabled={!tier}
                    onClick={() => setStep(2)}
                    className="min-w-[200px]"
                  >
                    Continue →
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Application Form */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-black">
                    {tier === 'pro' ? 'Pro' : 'Neighbour'} Tier Application
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                    ← Change tier
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Info */}
                  <Card variant="solid">
                    <h4 className="font-black mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-brand-500" />
                      Personal Information
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-bold mb-2">Full Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          placeholder="John Doe"
                          className="w-full rounded-xl border-2 border-neutral-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold mb-2">Email *</label>
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="john@example.com"
                            className="w-full rounded-xl border-2 border-neutral-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-bold mb-2">Phone *</label>
                          <input
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="+1 (613) 555-1234"
                            className="w-full rounded-xl border-2 border-neutral-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Location */}
                  <Card variant="solid">
                    <h4 className="font-black mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-brand-500" />
                      Location & Service Area
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-bold mb-2">City *</label>
                        <select
                          required
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="w-full rounded-xl border-2 border-neutral-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        >
                          <option value="">Select your city</option>
                          {provinces.map((province) => {
                            const provinceCities = getCitiesByProvince(
                              canadianCities.find(c => c.province === province)?.provinceCode || ''
                            );
                            return provinceCities.length > 0 ? (
                              <optgroup key={province} label={province}>
                                {provinceCities.map((city) => (
                                  <option key={city.slug} value={city.slug}>
                                    {city.name}
                                  </option>
                                ))}
                              </optgroup>
                            ) : null;
                          })}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-2">Address *</label>
                        <input
                          type="text"
                          required
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder="123 Main St"
                          className="w-full rounded-xl border-2 border-neutral-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Equipment */}
                  <Card variant="solid">
                    <h4 className="font-black mb-4 flex items-center gap-2">
                      <Truck className="w-5 h-5 text-brand-500" />
                      Equipment & Requirements
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-bold mb-2">Equipment Type *</label>
                        <select
                          required
                          value={formData.equipment}
                          onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                          className="w-full rounded-xl border-2 border-neutral-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        >
                          <option value="">Select equipment</option>
                          <option value="shovel">Shovel</option>
                          <option value="single-stage">Single-stage snowblower</option>
                          <option value="two-stage">Two-stage snowblower</option>
                          <option value="plow-truck">Plow truck</option>
                        </select>
                      </div>

                      {tier === 'pro' && (
                        <>
                          <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl">
                            <input
                              type="checkbox"
                              id="insurance"
                              checked={formData.hasInsurance}
                              onChange={(e) => setFormData({ ...formData, hasInsurance: e.target.checked })}
                              className="mt-1 w-5 h-5 text-accent-500 rounded focus:ring-2 focus:ring-accent-500"
                              required
                            />
                            <label htmlFor="insurance" className="text-sm cursor-pointer">
                              <strong>I have liability insurance</strong> and can provide proof of coverage
                            </label>
                          </div>

                          <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl">
                            <input
                              type="checkbox"
                              id="license"
                              checked={formData.hasLicense}
                              onChange={(e) => setFormData({ ...formData, hasLicense: e.target.checked })}
                              className="mt-1 w-5 h-5 text-accent-500 rounded focus:ring-2 focus:ring-accent-500"
                              required
                            />
                            <label htmlFor="license" className="text-sm cursor-pointer">
                              <strong>I have a business license</strong> and am GST registered
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  </Card>

                  {/* Background Check Notice */}
                  <Card variant="outline" className="border-2 border-brand-500 bg-brand-50">
                    <div className="flex items-start gap-4">
                      <Shield className="w-6 h-6 text-brand-500 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold mb-2">Background Check Required</h4>
                        <p className="text-sm text-neutral-700 mb-3">
                          All operators must pass a background check via Stripe Identity. This typically takes 24-48 hours 
                          and verifies your identity and criminal record. You'll receive an email with instructions after submitting this application.
                        </p>
                        <p className="text-xs text-neutral-600">
                          We take safety seriously. Background checks help build trust in our community.
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Submit */}
                  <div className="flex items-center justify-between pt-6 border-t">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setStep(1)}
                    >
                      ← Back
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="bg-accent-500 hover:bg-accent-600 shadow-glow-green"
                    >
                      Submit Application →
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}
          </div>
        )}
      </Section>
    </>
  );
}

